import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Typography,
  TextareaAutosize,
  Box,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { env } from "../../utilities/function";
import { useMessage } from "../Header";
import { getCookie } from "../../utilities/cookies";
import axios from "axios";
import { getDocumentByIdAndUserId } from "../../pages/document/Functions/document";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  contentBox: {
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    display: "flex",
    width: "450px",
    flexDirection: "column",
    gap: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    alignItems: "center",
  },
  textarea: {
    width: "387px",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    fontSize: "1rem",
    resize: "vertical",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    "&::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 1, // Fix for Firefox
    },
  },
  button: {
    borderRadius: theme.shape.borderRadius,
    alignSelf: "center",
    width: "60%",
  },
  loaderBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: "40px 0",
  },
}));

const SignedFinalStep = ({
  pdfFile,
  handleClose,
  signedType,
  documentData,
  otherSignerEmail,
  userEmail,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { showError, showSuccess } = useMessage();
  const [loading, setLoading] = useState(false);

  const [documentInformation, setDocumentInformation] = useState({
    documentTitle: pdfFile?.name || "",
    optionalMessage: "",
  });
  const [documentDetails, setDocumentDetails] = useState(undefined);
  const documentId = getCookie("documentId");
  // console.log(signedType);

  const [autoTriggered, setAutoTriggered] = useState(false);

  const onChangeHandller = (e) => {
    setDocumentInformation((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const getDocumentDetails = async (documentId) => {
    try {
      const response = await getDocumentByIdAndUserId(documentId);
      if (response.status === 200) {
        setDocumentDetails(response.data);
        setDocumentInformation((prev) => ({
          ...prev,
          documentTitle: response.data.fileName,
        }));
      } else {
        showError("Error fetching documents");
      }
    } catch (error) {
      showError("Error fetching documents");
    }
  };

  useEffect(() => {
    if (documentId) getDocumentDetails(documentId);
  }, [documentId]);

  const signADocumentHandller = async () => {
    setLoading(true);

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const data = {
        documentTitle: documentInformation.documentTitle,
        optionalMessage: documentInformation.optionalMessage,
        documentDetails,
        otherSignerEmail,
        userEmail: user?.recoveryEmail || userEmail,
      };

      if (documentDetails?.viewers) {
        data.documentDetails.viewers = documentDetails.viewers.map(
          (item) => item.email
        );
      }

      const response = await axios.post(
        env("BACKEND_SERVER") + "/document/sendMail",
        data,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        }
      );

      if (response.data.status === 200) {
        showSuccess("Document Signed successfully!");
        if (typeof handleClose === "function") handleClose();
      } else {
        showError("Error while signing document");
      }
    } catch (error) {
      // console.error(error);
      showError("Error while signing document");
    } finally {
      setLoading(false);
    }
  };

  const isOtherSignerRoute =
    typeof window !== "undefined" &&
    window.location.pathname === "/othersigner";

  // New flag: auto-sign either when on /othersigner OR when signedType is "yourself"
  const shouldAutoSign = isOtherSignerRoute || signedType === "yourself";

  // auto-trigger sign when on /othersigner or signedType === 'yourself' and documentDetails available
  useEffect(() => {
    if (shouldAutoSign && documentDetails && !autoTriggered) {
      signADocumentHandller();
      setAutoTriggered(true);
    }
  }, [shouldAutoSign, documentDetails, autoTriggered]);

  // If route is /othersigner or signedType is 'yourself': render the component-sized loader (centered inside the component box)
  if (shouldAutoSign) {
    return (
      <Box className={classes.container}>
        <Box className={classes.contentBox}>
          <Box className={classes.loaderBox}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
              Processing document...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // Normal render when not auto-signing
  return (
    <Box className={classes.container}>
      <Box className={classes.contentBox}>
        <Typography
          variant="h5"
          align="center"
          fontWeight={600}
          color="text.primary"
        >
          Final Step
        </Typography>

        <TextField
          fullWidth
          label="Document Title"
          name="documentTitle"
          variant="outlined"
          disabled={true}
          value={documentInformation.documentTitle || ""}
          onChange={onChangeHandller}
          InputProps={{
            style: {
              color: theme.palette.text.primary,
            },
          }}
          InputLabelProps={{
            style: {
              color: theme.palette.text.secondary,
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: theme.palette.divider,
              },
              "&:hover fieldset": {
                borderColor: theme.palette.action.hover,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: theme.palette.text.secondary,
              opacity: 1,
            },
          }}
        />

        <Box>
          <Typography variant="subtitle1" gutterBottom color="text.primary">
            Message (optional)
          </Typography>
          <TextareaAutosize
            name="optionalMessage"
            minRows={4}
            placeholder="Add an optional message for other document signers..."
            value={documentInformation.optionalMessage}
            onChange={onChangeHandller}
            className={classes.textarea}
          />
        </Box>

        <Button
          className={classes.button}
          variant="contained"
          color="primary"
          disabled={loading}
          onClick={signADocumentHandller}
        >
          {signedType == "send" && otherSignerEmail && userEmail
            ? "Sign Document"
            : ""}

          {signedType == "send" && otherSignerEmail && userEmail
            ? ""
            : signedType === "yourself"
            ? "Sign Document"
            : signedType === "signandsend"
            ? "Sign & Send for Signature"
            : "Send for Signature"}
        </Button>
      </Box>
    </Box>
  );
};

export default SignedFinalStep;
